import { AnimatePresence, motion } from 'framer-motion'
import { Eye, EyeOff, Filter, X } from 'lucide-react'
import { cn } from '@/utils/cn'

export type EntityTypeStat = {
  type: string
  count: number
  isHidden: boolean
  color: { fill: string; stroke: string }
}

interface EntityTypeFilterPanelProps {
  open: boolean
  stats: EntityTypeStat[]
  hiddenCount: number
  onToggle: (type: string) => void
  onShowAll: () => void
  onClose: () => void
  /** Where the panel attaches, in pixels from the right edge of the positioned parent. */
  rightOffset?: number
  /** Where the panel starts, in pixels from the top of the positioned parent. */
  topOffset?: number
  emptyLabel?: string
  /** Width of the panel; defaults to 300px. */
  widthPx?: number
}

/**
 * Floating "hide/show entity types" filter. Shared between the Map view
 * (geolocation-view.tsx) and the Time Axis view (time-axis.tsx) — both
 * surface the same "click a type to hide it" UX driven by per-view state.
 *
 * The component renders ONLY the panel; toggle button and positioning are
 * the caller's responsibility so each view can place the trigger where it
 * makes sense in that layout.
 */
export function EntityTypeFilterPanel({
  open,
  stats,
  hiddenCount,
  onToggle,
  onShowAll,
  onClose,
  rightOffset = 16,
  topOffset = 12,
  emptyLabel = 'No entities to filter.',
  widthPx = 300,
}: EntityTypeFilterPanelProps) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.aside
          key="entity-type-filter-panel"
          initial={{ opacity: 0, x: 32, scale: 0.94, filter: 'blur(10px)' }}
          animate={{ opacity: 1, x: 0, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, x: 24, scale: 0.96, filter: 'blur(8px)' }}
          transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
          style={{
            right: rightOffset,
            top: topOffset,
            width: widthPx,
            // Never wider than the room left of `rightOffset`, minus a 12px gutter.
            maxWidth: `calc(100% - ${rightOffset + 12}px)`,
            transformOrigin: 'right top',
          }}
          className={cn(
            'pointer-events-auto absolute z-20 flex max-h-[55vh] flex-col overflow-hidden rounded-[20px]',
            'border border-border bg-accent text-accent-foreground',
            'shadow-[0_24px_72px_-18px_hsl(var(--foreground)/0.40),0_6px_18px_-6px_hsl(var(--foreground)/0.22),inset_0_1px_0_hsl(0_0%_100%/0.08)]'
          )}
        >
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-semibold">Filters</span>
              {hiddenCount > 0 && (
                <span className="rounded-full bg-background/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {hiddenCount} hidden
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground transition-colors hover:text-foreground max-md:-m-2.5 max-md:p-2.5"
              aria-label="Close filter"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {stats.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {emptyLabel}
              </div>
            ) : (
              <div className="space-y-0.5">
                {stats.map(({ type, count, isHidden, color }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => onToggle(type)}
                    className={cn(
                      'group flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left transition-colors max-md:py-2',
                      'hover:bg-background/40',
                      isHidden && 'opacity-50'
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className="size-2.5 shrink-0 rounded-full border"
                        style={{ backgroundColor: color.fill, borderColor: color.stroke }}
                      />
                      <span className="truncate text-sm">{type}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-muted-foreground">
                      <span className="text-[11px]">{count}</span>
                      {isHidden ? (
                        <EyeOff className="h-3.5 w-3.5" />
                      ) : (
                        <Eye className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100 max-md:opacity-100" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {hiddenCount > 0 && (
            <div className="border-t border-border/60 px-3 py-2">
              <button
                type="button"
                onClick={onShowAll}
                className="w-full rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-background/40 hover:text-foreground max-md:py-2.5"
              >
                Show all
              </button>
            </div>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  )
}
